// Booking display helpers (P2.4a): times shown in the booking's own IANA zone,
// datetime inputs read in that zone, and money in minor units by the
// currency's ISO exponent. Pure functions, unit-tested.

/** The zone's offset from UTC (ms) at a given instant. */
function tzOffsetMs(ts: number, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(ts));
  const n = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour') % 24, n('minute'), n('second'));
  return asUtc - Math.floor(ts / 1000) * 1000;
}

/** A datetime-local value ("YYYY-MM-DDTHH:mm") read as wall time in `tz` → RFC 3339 UTC. */
export function zonedLocalToIso(local: string, tz: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) return '';
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  let ts = guess - tzOffsetMs(guess, tz);
  const again = guess - tzOffsetMs(ts, tz);
  if (again !== ts) ts = again;
  return new Date(ts).toISOString().replace('.000Z', 'Z');
}

/** An instant → the datetime-local value of its wall time in `tz`. */
export function isoToZonedLocal(iso: string | null | undefined, tz: string): string {
  if (!iso) return '';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  return new Date(ts + tzOffsetMs(ts, tz)).toISOString().slice(0, 16);
}

/** An instant shown in the booking's zone, e.g. "4 Nov 2026, 14:00". */
export function formatInZone(iso: string | null | undefined, tz: string, locale = 'en-GB'): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { timeZone: tz, dateStyle: 'medium', timeStyle: 'short' }).format(d);
  } catch {
    return d.toISOString();
  }
}

/** The currency's minor-unit digits (ISO 4217 exponent): TZS 2, JPY 0, … */
export function currencyDigits(currency: string): number {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

/** Minor units → "TZS 150,000.00". */
export function formatMinor(minor: number | null | undefined, currency: string | null | undefined): string {
  if (minor == null || !currency) return '—';
  const digits = currencyDigits(currency);
  const major = minor / 10 ** digits;
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'code' }).format(major);
  } catch {
    return `${currency} ${major.toFixed(digits)}`;
  }
}

/** A typed major amount ("150000" or "150,000.50") → minor units; null if not a valid amount. */
export function parseMajorToMinor(text: string, currency: string): number | null {
  const s = text.replace(/[,\s]/g, '');
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const digits = currencyDigits(currency || 'TZS');
  const [whole, frac = ''] = s.split('.');
  if (frac.length > digits) return null;
  return Number(whole) * 10 ** digits + Number((frac + '0'.repeat(digits)).slice(0, digits) || '0');
}

/** Minor units → the plain major text an input shows ("150000.00"). */
export function minorToMajorText(minor: number, currency: string): string {
  const digits = currencyDigits(currency || 'TZS');
  return (minor / 10 ** digits).toFixed(digits);
}

// ── terms amounts (F): a `fixed` value is integer minor units of the terms'
// currency on the wire; a `percent` value is 0–100 as typed. ─────────────────

export type TermsBasis = 'percent' | 'fixed';

/** A stored terms value → what its input shows (minor → major for fixed). */
export function termsValueToText(basis: TermsBasis | '' | null | undefined, value: number | null | undefined,
  currency: string | null | undefined): string {
  if (value == null) return '';
  if (basis === 'fixed' && currency) return minorToMajorText(value, currency);
  return String(value);
}

export type TermsValueError = 'currency' | 'amount' | 'percent';

/** A typed terms value → the wire value. Fixed: major → minor by the currency's
 *  digits, REFUSING extra decimals (never rounded). Empty text → no value. */
export function termsTextToValue(basis: TermsBasis, text: string, currency: string | null | undefined):
  { value?: number; error?: TermsValueError } {
  const t = text.trim();
  if (!t) return {};
  if (basis === 'fixed') {
    const cur = (currency ?? '').trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(cur)) return { error: 'currency' };
    const minor = parseMajorToMinor(t, cur);
    return minor == null ? { error: 'amount' } : { value: minor };
  }
  const n = Number(t);
  if (!/^\d+(\.\d+)?$/.test(t) || n > 100) return { error: 'percent' };
  return { value: n };
}

/** A terms value for display: fixed → "TZS 500.00" (like the fee), percent → "50%". */
export function formatTermsValue(basis: TermsBasis | null | undefined, value: number | null | undefined,
  currency: string | null | undefined): string {
  if (basis == null || value == null) return '—';
  return basis === 'fixed' ? formatMinor(value, currency) : `${value}%`;
}

export const BOOKING_DEFAULT_TZ = 'Africa/Dar_es_Salaam';
